import React, { useState } from 'react';
import { Button, Form, Header, Icon, Input, Modal } from 'semantic-ui-react';
import styled from 'styled-components';
import { CreditasResponsible } from '../../types/CreditasResponsible';
import { maskCpf } from '../../shared/documents/cpf';
import { maskDate, maskPhoneNumber } from '../../services/masks';
import {
  CreditasResponsibleErrors,
  validateCreditasResponsible
} from '../../utils/creditasResponsible';

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 24px;
  margin-top: 24px;
  .field {
    margin: 0 !important;
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

interface Props {
  value: CreditasResponsible;
  onSave: (value: CreditasResponsible) => void;
  onClose: () => void;
  disabled?: boolean;
}

const fields: {
  key: keyof CreditasResponsible;
  label: string;
  maxLength?: number;
  inputMode?: 'numeric' | 'tel' | 'email';
}[] = [
  { key: 'responsibleName', label: 'Nome do responsável', maxLength: 255 },
  { key: 'responsibleCpf', label: 'CPF', inputMode: 'numeric', maxLength: 14 },
  { key: 'responsibleMotherName', label: 'Nome da mãe', maxLength: 255 },
  {
    key: 'responsibleBirthDate',
    label: 'Data de nascimento',
    inputMode: 'numeric',
    maxLength: 10
  },
  { key: 'responsibleEmail', label: 'E-mail', inputMode: 'email', maxLength: 255 },
  { key: 'responsiblePhone', label: 'Telefone', inputMode: 'tel', maxLength: 15 }
];

export const CreditasConfigModal = ({
  value,
  onSave,
  onClose,
  disabled = false
}: Props) => {
  const [draft, setDraft] = useState<CreditasResponsible>({ ...value });
  const [errors, setErrors] = useState<CreditasResponsibleErrors>({});

  const change = (key: keyof CreditasResponsible, raw: string) => {
    let next = raw;
    if (key === 'responsibleCpf') next = maskCpf(raw);
    if (key === 'responsiblePhone')
      next = maskPhoneNumber(raw.replace(/\D/g, '').slice(0, 11));
    if (key === 'responsibleBirthDate')
      next = maskDate(raw.replace(/\D/g, '').slice(0, 8));
    setDraft(current => ({ ...current, [key]: next }));
    setErrors(current => ({ ...current, [key]: undefined }));
  };

  const save = () => {
    if (disabled) return;
    const nextErrors = validateCreditasResponsible(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onSave({
      ...draft,
      responsibleName: draft.responsibleName.trim(),
      responsibleMotherName: draft.responsibleMotherName.trim(),
      responsibleEmail: draft.responsibleEmail.trim()
    });
    onClose();
  };

  return (
    <Modal
      open
      closeIcon
      onClose={onClose}
      style={{ height: 'auto', width: '694px', maxWidth: 'calc(100vw - 32px)' }}
    >
      <Modal.Header>
        <Icon name="cog" /> Creditas
      </Modal.Header>
      <Modal.Content scrolling>
        <Header as="h4" style={{ marginBottom: 0 }}>
          CONFIGURAÇÕES DA SEGURADORA
        </Header>
        <p>Altere os parâmetros de configuração da seguradora.</p>
        <hr style={{ border: 0, borderTop: '1px solid #ddd' }} />
        <Form as="div">
          <Fields>
            {fields.map(({ key, label, maxLength, inputMode }) => (
              <Form.Field key={key} error={!!errors[key]}>
                <label htmlFor={`creditas-${key}`}>
                  {label}{' '}
                  <span style={{ color: '#db2828' }} aria-hidden="true">
                    *
                  </span>
                </label>
                <Input
                  id={`creditas-${key}`}
                  name={key}
                  fluid
                  value={draft[key] || ''}
                  disabled={disabled}
                  maxLength={maxLength}
                  inputMode={inputMode}
                  onChange={(_event, data) => change(key, data.value)}
                  aria-required="true"
                  aria-invalid={!!errors[key]}
                  aria-describedby={errors[key] ? `creditas-error-${key}` : undefined}
                />
                {errors[key] && (
                  <span
                    id={`creditas-error-${key}`}
                    role="alert"
                    style={{ color: '#9f3a38', fontSize: '12px' }}
                  >
                    {errors[key]}
                  </span>
                )}
              </Form.Field>
            ))}
          </Fields>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" color="red" onClick={onClose}>
          <Icon name="remove" /> Cancelar
        </Button>
        <Button type="button" color="green" disabled={disabled} onClick={save}>
          <Icon name="checkmark" /> Salvar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
